// src/components/FileItemCard.tsx
import React from "react";
import {
  Paper,
  Stack,
  Typography,
  Chip,
  Box,
  Tooltip,
  IconButton,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useNavigate } from "react-router-dom";
import type { FileRow } from "../types/file";

interface Props {
  file: FileRow;
  onDelete?: (file: FileRow) => void;
}

// UTC+0 포맷터
const formatUtc = (value?: string | null) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);

  const pad = (n: number) => String(n).padStart(2, "0");
  const year = d.getUTCFullYear();
  const month = pad(d.getUTCMonth() + 1);
  const day = pad(d.getUTCDate());
  const hour = pad(d.getUTCHours());
  const minute = pad(d.getUTCMinutes());
  const second = pad(d.getUTCSeconds());

  return `${year}-${month}-${day} ${hour}:${minute}:${second} (UTC+0)`;
};

const FileItemCard: React.FC<Props> = ({ file, onDelete }) => {
  const navigate = useNavigate();
  const isPatch = (file.version_kind || "").toLowerCase() === "patch";

  // 상태별 색상
  const getStatusColor = (status?: string) => {
    switch (status) {
      case "ready": return "success";
      case "processing": return "warning";
      case "error": return "error";
      default: return "default";
    }
  };

  const handleClickCard = () => {
    navigate(`/files/${file.file_id}`);
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        mb: 1,
        cursor: "pointer",
        transition: "background-color 0.2s",
        "&:hover": {
          bgcolor: "action.hover",
        },
        display: "flex",
        alignItems: "center",
        width: "100%", // ✅ 가로 전체 사용
      }}
      onClick={handleClickCard}
    >
      {/* 1. 아이콘 */}
      <DescriptionIcon color="action" sx={{ mr: 2 }} />

      {/* 2. 메인 정보 (파일명 + 버전 + 태그들) */}
      <Box sx={{ flexGrow: 1, minWidth: 0, mr: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
          
          {/* 파일명 */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mr: 1 }}>
            {file.original_name}
          </Typography>

          {/* 버전 & 타입 뱃지 */}
          <Chip label={`v${file.version_no}`} size="small" variant="filled" sx={{ height: 20, fontSize: '0.7rem' }} />
          <Chip 
            label={isPatch ? "PATCH" : "FULL"} 
            color={isPatch ? "warning" : "primary"} 
            size="small" 
            variant="outlined"
            sx={{ height: 20, fontSize: '0.7rem' }} 
          />

          {/* ✅ 상태 뱃지 (복구됨) */}
          {file.status && (
            <Chip
              label={file.status}
              color={getStatusColor(file.status)}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
        </Stack>

        {/* 3. 부가 정보 (날짜 등 - 아래 줄) */}
        <Stack direction="row" spacing={2} sx={{ mt: 0.5, color: "text.secondary" }}>
          {file.effective_date && (
            <Typography variant="caption">
              eff: {formatUtc(file.effective_date)}
            </Typography>
          )}
          {file.uploaded_at && (
            <Typography variant="caption">
              up: {formatUtc(file.uploaded_at)}
            </Typography>
          )}
          {file.base_file_id && (
            <Typography variant="caption">
              base: {file.base_file_id}
            </Typography>
          )}
        </Stack>
      </Box>

      {/* 4. 우측 액션 버튼 */}
      {onDelete && (
        <Tooltip title="이 버전 삭제">
          <IconButton
            size="small"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(file);
            }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Paper>
  );
};

export default FileItemCard;